import React, { useEffect, PropsWithChildren } from 'react';
import { userActions } from '../../state/redux/slice/user';
import store from '../../state/redux/store';
import { useHistory } from 'src/router'
// AMPLIFY
import { Amplify } from 'aws-amplify';
import { amplifyConfig } from '@alucio/aws-beacon-amplify';
import { useAuthenticator } from '@aws-amplify/ui-react-native';
import { fetchAuthSession } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { Authenticator as AmplifyAuthenticator } from './Authenticator';
import config from 'src/config/app.json';

// Custom Authentication Screens
import SignIn from 'src/screens/Authentication/SignIn/SignIn';
import ForgotPassword from 'src/screens/Authentication/Password/ForgotPassword/ForgotPassword';
import { useAppSettings } from 'src/state/context/AppSettings';
import Loading from 'src/screens/Loading';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { generateZendeskURL } from 'src/utils/zendeskHelpers';
import { SignedInProvider } from './services/SignedStateProvider';
import * as logger from 'src/utils/logger'
import { beaconCognitoTokenProvider } from 'src/classes/auth/BeaconCognitoTokenProvider';
import { beaconCredentialsAndIdentityIdProvider } from 'src/classes/auth/BeaconCredentialsAndIdentityIdProvider';

amplifyConfig.oauth = {
  domain: `${config.authUrl}.auth.us-west-2.amazoncognito.com`,
  scope: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
  redirectSignIn: `${window.location.origin}/`,
  redirectSignOut: `${window.location.origin}/`,
  responseType: 'code',
}

/*
 * SSO Relay State Handling
 * This needs to exist outside of react since the customOAuthState event is triggered
 * before react loads. We save the relayState value and push it into the history
 */
let ssoRelayState = '';
Hub.listen('auth', (data: any) => {
  if (data.payload.event === 'customOAuthState') {
    ssoRelayState = data.payload.data;
  }
}, 'AuthListenerApptsx');

Amplify.configure(amplifyConfig, {
  API: {
    GraphQL: {
      headers: async () => {
        return new Promise((resolve) => {
          fetchAuthSession().then((session) => {
            // Only override the auth header if we are actually signed in and have tokens
            if (session.tokens) {
              resolve({ Authorization: session.tokens?.idToken?.toString() })
            } else {
              resolve({})
            }
          }).catch(() => {
            resolve({})
          })
        })
      },
    },
  },
  Auth: {
    tokenProvider: beaconCognitoTokenProvider,
    credentialsProvider: beaconCredentialsAndIdentityIdProvider,
  },
});
beaconCognitoTokenProvider.setAuthConfig(Amplify.getConfig().Auth!!)

interface AuthProps {
}

// Alternatively, don't use the HOC and use the components instead
// https://aws-amplify.github.io/docs/js/authentication#using-the-authenticator-component
const Authenticator: React.FC<PropsWithChildren<AuthProps>> = props => {
  const { children } = props;
  const { initialUrlSearchParams, dispatch: settingsDispatch } = useAppSettings();
  const history = useHistory()
  const returnTo = initialUrlSearchParams.get('return_to')
  const enableZendeskKnowledgebase = useFeatureFlags('enableZendeskKnowledgebase');
  const zendeskURL = useFeatureFlags('BEAC_7431_dynamic_zendesk_url');

  const { user, authStatus } = useAuthenticator();
  logger.auth.signIn.authenticator.debug('Authenticator Rendered')
  useEffect(() => {
    if (ssoRelayState) {
      history.push(ssoRelayState)
    }
  }, [])

  useEffect(() => {
    logger.auth.signIn.authenticator.debug('useEffect onAuthState/authData Called')
    const init = async () => {
      const userSession = await fetchAuthSession();
      if (userSession.tokens?.idToken) {
        const idTokenAttributes = userSession.tokens?.idToken?.payload ?? {}
        store.dispatch(
          userActions.setCognitoUser({ username: idTokenAttributes.email, attributes: idTokenAttributes }),
        );
        if (Array.isArray(idTokenAttributes.identities) && idTokenAttributes.identities.length > 0) {
          settingsDispatch({
            type: 'setIsSSO',
          })
        }

        if (returnTo && enableZendeskKnowledgebase) {
          const url = await generateZendeskURL(zendeskURL, returnTo);
          window.location.href = url;
        }
      } else {
        logger.auth.signIn.authenticator.error('Unable to retrieve idToken even though authStatus is authenticated ')
      }
    }
    if (authStatus === 'authenticated') {
      init()
    }
  }, [authStatus, returnTo, user, enableZendeskKnowledgebase]);

  if (returnTo && enableZendeskKnowledgebase) {
    return <Loading context="Redirect Authenticator" />
  } else if (authStatus === 'authenticated') {
    return (<>{children}</>)
  } else {
    return (
      <AmplifyAuthenticator
        components={components}
      />
    )
  }
};

const components = {
  SignIn: SignIn,
  ForgotPassword: ForgotPassword,
};

const AuthenticatorWrapper: React.FC<PropsWithChildren> = (props) => {
  return (
    <SignedInProvider>
      <AmplifyAuthenticator.Provider>
        <Authenticator>
          {props.children}
        </Authenticator>
      </AmplifyAuthenticator.Provider>
    </SignedInProvider>
  );
};

export default AuthenticatorWrapper;
